/**
 * Billing page — always separates the three money lines:
 *   1. ZeroFare subscription fee (fixed plan price)
 *   2. Customer provider charge (direct, provider-billed)
 *   3. Sponsored capacity (ZeroFare-funded, never billed to the tenant)
 * Fixture-driven states render from counsel-approved fixtures only.
 */

import type { ReactElement } from "react";

export interface BillingFixture {
  readonly subscriptionFeeMicros: number;
  readonly planName: string;
  readonly providerChargeMicros: number;
  readonly sponsoredCapacityMicros: number;
  readonly state:
    | "active"
    | "trialing"
    | "past_due"
    | "canceled"
    | "hosted_cutoff"
    | "deleted"
    | "none";
  readonly paidThrough?: number;
  readonly graceEndsAt?: number;
}

export function BillingPage({ fixture }: { fixture: BillingFixture }): ReactElement {
  return (
    <div className="page page-billing">
      <h1>Billing</h1>
      <table className="billing-lines">
        <caption>Three separate money lines — never merged</caption>
        <thead>
          <tr>
            <th scope="col">Line</th>
            <th scope="col">Amount</th>
            <th scope="col">Billed by</th>
          </tr>
        </thead>
        <tbody>
          <tr className="billing-subscription">
            <th scope="row">ZeroFare subscription ({fixture.planName})</th>
            <td>{(fixture.subscriptionFeeMicros / 1_000_000).toFixed(2)} USD / month</td>
            <td>ZeroFare</td>
          </tr>
          <tr className="billing-provider">
            <th scope="row">Customer provider charge</th>
            <td>{(fixture.providerChargeMicros / 1_000_000).toFixed(2)} USD</td>
            <td>Your provider, directly</td>
          </tr>
          <tr className="billing-sponsored">
            <th scope="row">Sponsored capacity</th>
            <td>{(fixture.sponsoredCapacityMicros / 1_000_000).toFixed(2)} USD</td>
            <td>ZeroFare — not billed to you</td>
          </tr>
        </tbody>
      </table>
      <p className="billing-state">Subscription state: {fixture.state}</p>
      {fixture.paidThrough !== undefined ? (
        <p className="billing-paid-through">Paid through {new Date(fixture.paidThrough).toISOString()}</p>
      ) : null}
      {fixture.graceEndsAt !== undefined ? (
        <p className="billing-grace">Grace period ends {new Date(fixture.graceEndsAt).toISOString()}</p>
      ) : null}
    </div>
  );
}
